import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../api/api';
import { QueryDto, EntityType, CustomProperty } from '../api/generated/data-contracts';
import { HttpResponse } from '../api/generated/http-client';
import { ContentConfigurationType } from '../Entities/ContentConfiguration/ContentConfigurationType';
import { LookupType } from '../Entities/ContentConfiguration/Lookup/LookupType';
import { settings } from '../context/network/http/QueryProvider/queries/UISettings';
import { CUSTOM_FIELDS_WITH_LOOKUPS } from '../context/network/http/QueryProvider/queryKeys';

interface LookupInfo {
    id: string;
    name: string;
    lookupType: LookupType;
}

interface CustomFieldWithLookupInfo extends CustomProperty {
    lookupInfo?: LookupInfo;
}

export interface CustomFieldsWithLookupsResult {
    resourceCustomFields: CustomFieldWithLookupInfo[];
    projectCustomFields: CustomFieldWithLookupInfo[];
    contractCustomFields: CustomFieldWithLookupInfo[];
    isLoading: boolean;
    error: any;
}

/**
 * Custom hook that fetches all custom fields and their lookup information.
 * This provides the lookup type information needed to determine which custom fields
 * should have tree-specific operators (like RBS fields).
 *
 * The query is cached indefinitely to avoid repeated API calls.
 */
export const useCustomFieldsWithLookups = (): CustomFieldsWithLookupsResult => {
    const queryKey = useMemo(() => [CUSTOM_FIELDS_WITH_LOOKUPS] as const, []);

    const { data, isLoading, error } = useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            // Fetch all lookups to get their types
            const lookupsDto = {
                entity: EntityType.ContentConfiguration,
                query: { expression: 'ContentConfigurationType == @0', params: [ContentConfigurationType.Lookup] },
            } as QueryDto;

            const lookupsResponse = (await ApiCalls.queryEntity(lookupsDto)) as HttpResponse<any[], any>;
            const lookups = lookupsResponse.data || [];

            // Create a map of lookup ID to lookup info
            const lookupMap = new Map<string, LookupInfo>();
            lookups.forEach((lookup: any) => {
                lookupMap.set(lookup.id, {
                    id: lookup.id,
                    name: lookup.name,
                    lookupType: lookup.lookupType,
                });
            });

            // Get custom fields from settings
            const currentSettings = settings();
            if (!currentSettings) {
                return {
                    resourceCustomFields: [],
                    projectCustomFields: [],
                    contractCustomFields: [],
                    lookupMap,
                };
            }

            // Enhance custom fields with lookup information
            const enhanceCustomFields = (customFields: CustomProperty[]): CustomFieldWithLookupInfo[] => {
                return (customFields || []).map(field => ({
                    ...field,
                    lookupInfo: field.lookup ? lookupMap.get(field.lookup) : undefined,
                }));
            };

            return {
                resourceCustomFields: enhanceCustomFields(currentSettings.resourceCustomProperties || []),
                projectCustomFields: enhanceCustomFields(currentSettings.projectCustomProperties || []),
                contractCustomFields: enhanceCustomFields(currentSettings.contractCustomProperties || []),
                lookupMap,
            };
        },
        // Cache indefinitely like other settings-related queries
        gcTime: Infinity,
        staleTime: Infinity,
    });

    return {
        resourceCustomFields: data?.resourceCustomFields || [],
        projectCustomFields: data?.projectCustomFields || [],
        contractCustomFields: data?.contractCustomFields || [],
        isLoading,
        error,
    };
};

/**
 * Helper function to check if a custom field should use tree-specific operators.
 * Takes the property key directly and extracts the field ID to match against custom fields.
 */
export const shouldUseTreeOperatorsFromPropertyKey = (
    propertyKey: string,
    field: { type?: string; customType?: number },
    customFieldsData: CustomFieldsWithLookupsResult,
    entityType: 'resource' | 'project' | 'contract',
): boolean => {
    // Only apply to custom choice fields
    if (field.type !== 'custom' || field.customType !== 4) {
        return false;
    }

    // Extract field ID from property key pattern: customProperties|fieldId|
    const regex = /customProperties\|([^|]+)\|/i;
    const match = regex.exec(propertyKey);
    if (!match) {
        return false;
    }

    const extractedFieldId = match[1];

    // Get the appropriate custom fields based on entity type
    let customFields: CustomFieldWithLookupInfo[] = [];
    switch (entityType) {
        case 'resource':
            customFields = customFieldsData.resourceCustomFields;
            break;
        case 'project':
            customFields = customFieldsData.projectCustomFields;
            break;
        case 'contract':
            customFields = customFieldsData.contractCustomFields;
            break;
    }

    // Find by extracted field ID (the property key contains the ID, not the internal name)
    const customField = customFields.find(cf => cf.id === extractedFieldId);

    // Check if it has a tree lookup
    return customField?.lookupInfo?.lookupType === LookupType.Tree;
};
